import * as React from "react";
import { BusinessReport } from "@/modules/snapshots/businessReports.types";
import { useGetSnapshotCollectionsQuery } from "@/modules/snapshots/snapshotCollection.api";
import { SnapshotCollectionsRow } from "@/modules/snapshots/snapshotCollections/components/list/row";
import { Loader } from "@/components/ui/loader";
import { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";
import { SnapshotCollectionsOutcomes } from "@/modules/snapshots/snapshotCollections/components/outcomes";
import { useSearchParams } from "react-router";

interface Props {
  selectedBusinessReport: BusinessReport;
}

const QUERY_KEY = "snapshotCollectionId";
const REFRESH_KEY = "refresh";

export const SnapshotCollectionsList: React.FC<Props> = ({ selectedBusinessReport }) => {
  const [selectedCollection, setSelectedCollection] = React.useState<SnapshotCollection | null>(null);
  const { data, isLoading, refetch } = useGetSnapshotCollectionsQuery();
  const [searchParams, setSearchParams] = useSearchParams();
  const snapshotCollectionId = searchParams.get(QUERY_KEY);
  const refresh = searchParams.get(REFRESH_KEY);

  const collections = (data || []).filter(row => row.business_report_id === selectedBusinessReport.id);

  React.useEffect(() => {
    if (collections.length > 0) {
      setSelectedCollection(collections.find(row => row.id === snapshotCollectionId) || collections[0]);
    } else {
      setSelectedCollection(null);
    }

    return () => {
      setSelectedCollection(null);
    };
  }, [collections, snapshotCollectionId]);

  React.useEffect(() => {
    if (refresh) {
      refetch();
      setSearchParams(prev => {
        prev.delete(REFRESH_KEY);
        return prev;
      });
    }
  }, [refetch, refresh, setSearchParams]);

  const handleSelect = (collection: SnapshotCollection) => {
    setSelectedCollection(collection);
    setSearchParams(prev => {
      prev.set(QUERY_KEY, collection.id);
      return prev;
    });
  };

  const sortedCollections = React.useMemo(
    () => [...collections].sort((a, b) => a.name.localeCompare(b.name)),
    [collections],
  );

  return (
    <div>
      <h3 className="mb-5 mr-2 text-2xl font-semibold">Collections</h3>

      {isLoading && <Loader />}
      {!isLoading && collections.length === 0 && (
        <div className="flex flex-col items-center justify-center">
          <h3 className="mb-5 text-lg font-semibold">No collections found</h3>
          <p className="text-gray-500">Create a new collection to get started.</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-6">
        {sortedCollections.map(collection => (
          <SnapshotCollectionsRow
            isSelected={selectedCollection?.id === collection.id}
            key={collection.id}
            handleSelect={handleSelect}
            snapshotCollection={collection}
          />
        ))}
      </div>
      {selectedCollection && <SnapshotCollectionsOutcomes selectedCollection={selectedCollection} />}
    </div>
  );
};
